import { createSelector } from "reselect";

import {
	type FullStackPack,
	type NodeId,
	type StackPackConfiguration,
	StackPackInstanceState,
	type StackPackIntegration,
} from "~/models/dto";
import {
	type RequestStatus,
	errorStatus,
	getRequestStatusValue,
	isErrorRequestStatus,
	isLoadingRequestStatus,
	isSuccessRequestStatus,
	loadingStatus,
	successStatus,
} from "~/tools/request-status";

import { isStackPackEnabled } from "../../../../bottom_content/stackpacks/StackPacksUtils";
import type { RootState } from "../../RootState";

import type { StackPacksState } from "./StackPacksState";

export const selectStackPacksState = (state: RootState): StackPacksState => state.entities.stackPacks;

export const selectStackPacks: (state: RootState) => Array<FullStackPack> | undefined = createSelector(
	[selectStackPacksState],
	(state) => getRequestStatusValue(state.stackPacksStatus),
);

export const selectStackPacksStatus: (state: RootState) => RequestStatus<Error, Array<FullStackPack>> | undefined =
	createSelector([selectStackPacksState], (state) => state.stackPacksStatus);
const passNameParamThrough = (_: RootState, name: string): string => name;

export const selectStackPackStatus: (
	state: RootState,
	name: string,
) => RequestStatus<Error, FullStackPack> | undefined = createSelector(
	[selectStackPacksState, passNameParamThrough],
	(state, name) => {
		const { stackPacksStatus } = state;
		if (!stackPacksStatus) {
			return undefined;
		}
		if (isErrorRequestStatus(stackPacksStatus)) {
			return errorStatus(stackPacksStatus.error);
		}
		if (isLoadingRequestStatus(stackPacksStatus)) {
			return loadingStatus();
		}
		const stackPack = getRequestStatusValue(stackPacksStatus)?.find((stackPack) => stackPack.name === name);
		if (!stackPack) {
			return errorStatus(new Error(`StackPack "${name}" does not exist`));
		}
		return successStatus<FullStackPack>(stackPack);
	},
);

export const selectStackPackConfigurations = createSelector([selectStackPacks], (stackPacks) =>
	stackPacks ? stackPacks.flatMap(({ configurations }) => configurations) : [],
);

const passIdParamThrough = (_: RootState, id: NodeId): NodeId => id;

export const selectStackPackConfiguration = createSelector(
	[selectStackPackConfigurations, passIdParamThrough],
	(configurations: Array<StackPackConfiguration>, configurationId: NodeId | undefined) =>
		configurations.find(({ id }) => id === configurationId),
);

const passIntegrationParamThrough = (_: RootState, name: string, integration: string): string => integration;

export const selectStackPackIntegrationStatus: (
	state: RootState,
	name: string,
	integration: string,
) => RequestStatus<Error, StackPackIntegration> | undefined = createSelector(
	[selectStackPackStatus, passNameParamThrough, passIntegrationParamThrough],
	(stackPackStatus, name, integration): RequestStatus<Error, StackPackIntegration> | undefined => {
		if (!stackPackStatus) {
			return undefined;
		}
		if (isErrorRequestStatus(stackPackStatus)) {
			return errorStatus(stackPackStatus.error);
		}
		if (isLoadingRequestStatus(stackPackStatus)) {
			return loadingStatus();
		}

		if (isSuccessRequestStatus(stackPackStatus)) {
			const stackPackIntegration: StackPackIntegration | undefined = stackPackStatus.value.integrations.find(
				(stackPackIntegration) => stackPackIntegration.name === integration,
			);
			if (!stackPackIntegration) {
				return errorStatus(new Error(`StackPack integration "${integration}" does not exist on StackPack "${name}"`));
			}

			return successStatus(stackPackIntegration);
		}
	},
);

export const selectNamesOfEnabledStackPacks: (state: RootState) => Array<string> = createSelector(
	selectStackPacks,
	(stackPacks) => (stackPacks || []).filter(isStackPackEnabled).map((stackpack) => stackpack.name),
);

export const selectHasEnabledStackPacks: (state: RootState) => boolean = createSelector(
	[selectStackPacks],
	(stackPacks) => stackPacks?.some(isStackPackEnabled) ?? false,
);

export const isStackPackUpgrading = createSelector(
	[selectStackPackStatus],
	(stackPackStatus) =>
		getRequestStatusValue(stackPackStatus)?.configurations.some(
			({ status }) => status === StackPackInstanceState.Upgrading,
		) ?? false,
);
